/**
 * PostgreSQL API Hooks with Firestore-compatible interface
 *
 * These hooks wrap the API hooks and transform responses to match
 * the existing Firestore snake_case interface. This allows components
 * to switch from Firestore to PostgreSQL without code changes.
 *
 * Usage: Simply change imports from '@/hooks/useLeads' to '@/hooks/api/useLeadsCompat'
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient, useApiClientWithMeta } from '@/lib/api-client';
import type {
  Lead as FirestoreLead,
  LeadDoc,
  LeadWithGenerations as FirestoreLeadWithGenerations,
  Generation as FirestoreGeneration,
  GenerationDoc,
  GeminiAnalysis,
} from '@/types/firestore';
import type { Lead as ApiLead, Generation as ApiGeneration, LeadWithGenerations as ApiLeadWithGenerations } from './useLeadsApi';

// ============================================
// Transform Functions (camelCase → snake_case)
// ============================================

function transformApiLeadToFirestore(lead: ApiLead, tenantId: string): FirestoreLead {
  // Safely parse createdAt - fallback to now if invalid
  const createdAtDate = lead.createdAt ? new Date(lead.createdAt) : new Date();
  const validCreatedAt = isNaN(createdAtDate.getTime()) ? new Date() : createdAtDate;

  return {
    id: lead.id,
    tenant_id: tenantId,
    client_name: lead.clientName,
    client_phone: lead.clientPhone,
    client_ip: lead.clientIp || undefined,
    verification_status: lead.verificationStatus,
    gemini_analysis: (lead.geminiAnalysis as GeminiAnalysis) || {
      room_type: lead.roomType || 'unknown',
      room_state: 'unknown',
      materials: [],
      notes: '',
    },
    created_at: { toDate: () => validCreatedAt } as any,
  };
}

function transformApiGenerationToFirestore(gen: ApiGeneration, leadId: string, tenantId: string): FirestoreGeneration {
  // Safely parse createdAt - fallback to now if invalid
  const createdAtDate = gen.createdAt ? new Date(gen.createdAt) : new Date();
  const validCreatedAt = isNaN(createdAtDate.getTime()) ? new Date() : createdAtDate;

  return {
    id: gen.id,
    lead_id: leadId,
    tenant_id: tenantId,
    original_image_path: gen.originalImagePath,
    generated_image_path: gen.generatedImagePath,
    watermarked_image_path: gen.watermarkedImagePath || '',
    prompt_parameters: (gen.promptParameters as GenerationDoc['prompt_parameters']) || { style: '' },
    quote_range_min: gen.quoteRangeMin || 0,
    quote_range_max: gen.quoteRangeMax || 0,
    created_by: gen.createdBy,
    created_at: { toDate: () => validCreatedAt } as any,
  };
}

function transformApiLeadWithGenerationsToFirestore(
  lead: ApiLeadWithGenerations,
  tenantId: string
): FirestoreLeadWithGenerations {
  const baseLead = transformApiLeadToFirestore(lead, tenantId);
  return {
    ...baseLead,
    generations: lead.generations.map(gen =>
      transformApiGenerationToFirestore(gen, lead.id, tenantId)
    ),
  };
}

// ============================================
// Compatible Hooks
// ============================================

/**
 * Fetch all leads for a tenant (PostgreSQL API with Firestore-compatible response)
 */
export function useLeads(tenantId: string | undefined) {
  const apiWithMeta = useApiClientWithMeta();

  return useQuery({
    queryKey: ['leads', tenantId],
    queryFn: async (): Promise<FirestoreLead[]> => {
      if (!tenantId) return [];

      const result = await apiWithMeta.get<ApiLead>(`/tenants/${tenantId}/leads?limit=100`);
      return result.data.map(lead => transformApiLeadToFirestore(lead, tenantId));
    },
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Fetch all leads with their generations (PostgreSQL API with Firestore-compatible response)
 */
export function useLeadsWithGenerations(tenantId: string | undefined) {
  const api = useApiClient();
  const apiWithMeta = useApiClientWithMeta();

  return useQuery({
    queryKey: ['leads-with-generations', tenantId],
    queryFn: async (): Promise<FirestoreLeadWithGenerations[]> => {
      if (!tenantId) return [];

      // Get all leads (backend max is 100)
      const result = await apiWithMeta.get<ApiLead>(`/tenants/${tenantId}/leads?limit=100`);

      // Fetch generations for each lead
      const leadsWithGenerations = await Promise.all(
        result.data.map(async (lead) => {
          try {
            const fullLead = await api.get<ApiLeadWithGenerations>(`/leads/${lead.id}`);
            return transformApiLeadWithGenerationsToFirestore(fullLead, tenantId);
          } catch {
            // If we can't get generations, return lead with empty generations
            return {
              ...transformApiLeadToFirestore(lead, tenantId),
              generations: [],
            };
          }
        })
      );

      return leadsWithGenerations;
    },
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Fetch a single lead with generations
 */
export function useLead(tenantId: string | undefined, leadId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ['lead', tenantId, leadId],
    queryFn: async (): Promise<FirestoreLeadWithGenerations | null> => {
      if (!tenantId || !leadId) return null;

      const lead = await api.get<ApiLeadWithGenerations>(`/leads/${leadId}`);
      return transformApiLeadWithGenerationsToFirestore(lead, tenantId);
    },
    enabled: !!tenantId && !!leadId,
  });
}

/**
 * Create a new lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: Omit<LeadDoc, 'created_at'>;
    }) => {
      // Transform snake_case to camelCase for API
      const apiData = {
        clientName: data.client_name,
        clientPhone: data.client_phone,
        clientEmail: undefined,
        geminiAnalysis: data.gemini_analysis,
        verificationStatus: data.verification_status,
      };

      const result = await api.post<ApiLead>('/leads', apiData);
      return result.id;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads', variables.tenantId] });
      queryClient.invalidateQueries({
        queryKey: ['leads-with-generations', variables.tenantId],
      });
    },
  });
}

/**
 * Update a lead
 */
export function useUpdateLead() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      leadId,
      data,
    }: {
      tenantId: string;
      leadId: string;
      data: Partial<Omit<LeadDoc, 'created_at'>>;
    }) => {
      // Transform snake_case to camelCase for API
      const apiData: Record<string, unknown> = {};
      if (data.client_name !== undefined) apiData.clientName = data.client_name;
      if (data.client_phone !== undefined) apiData.clientPhone = data.client_phone;
      if (data.verification_status !== undefined) apiData.verificationStatus = data.verification_status;
      if (data.gemini_analysis !== undefined) apiData.geminiAnalysis = data.gemini_analysis;

      await api.patch(`/leads/${leadId}`, apiData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads', variables.tenantId] });
      queryClient.invalidateQueries({
        queryKey: ['leads-with-generations', variables.tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ['lead', variables.tenantId, variables.leadId],
      });
    },
  });
}

/**
 * Delete a lead
 */
export function useDeleteLead() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      leadId,
    }: {
      tenantId: string;
      leadId: string;
    }) => {
      await api.delete(`/leads/${leadId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads', variables.tenantId] });
      queryClient.invalidateQueries({
        queryKey: ['leads-with-generations', variables.tenantId],
      });
    },
  });
}
